import { useState } from 'react';
import { isAxiosError } from 'axios';
import type { TerminalPeripheralConfig } from '@reform-lite/contracts';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/auth/auth-store';
import {
  PERIPHERAL_BLOCK_LABELS,
  AUTHORABLE_PERIPHERAL_BLOCKS,
  useUpdateProfilePeripheralConfig,
  type TerminalProfile,
} from '@/hooks/use-terminals';
import {
  BLOCK_DEFAULTS,
  PeripheralBlockFields,
  summarisePeripheralBlock,
} from './peripheral-fields';
import { useT } from '@/i18n/use-t';

/**
 * RFL-241 — the group half of hardware authoring.
 *
 * This is where a store says "every lane on this profile prints to
 * /dev/usb/lp0 at 48 columns in cp1251", so a new terminal is configured by
 * being bound to the profile rather than by someone typing four values at the
 * lane. The per-terminal screen then handles the exceptions.
 *
 * Whole-bag replacement with its own version token, matching the endpoint.
 * The token is separate from the config bag's on purpose: hardware is authored
 * by whoever is standing next to the printer, and tax rules by someone else,
 * so they must not invalidate each other's open forms.
 */
export function ProfilePeripheralEditor({ profile }: { profile: TerminalProfile }) {
  const t = useT('admin_ui');
  const canAdmin = useHasPermission('terminal-management.admin');
  const update = useUpdateProfilePeripheralConfig();
  const stored = (profile.peripheralConfig ?? {}) as TerminalPeripheralConfig;
  const [draft, setDraft] = useState<TerminalPeripheralConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const config = draft ?? stored;
  const dirty = draft !== null;
  const blocks = AUTHORABLE_PERIPHERAL_BLOCKS;

  if (!open) {
    const authored = blocks.filter((b) => config[b] !== undefined);
    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="text-muted-foreground">
          {authored.length === 0
            ? t(
                'terminal.hardware.profile.none',
                'No group hardware authored — every terminal uses its own defaults.',
              )
            : `${t('terminal.hardware.profile.summary', 'Group hardware')}: ${authored.map((b) => t(`terminal.hardware.block.${b}`, PERIPHERAL_BLOCK_LABELS[b])).join(', ')}`}
        </span>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          {canAdmin
            ? t('terminal.hardware.profile.edit', 'Edit hardware')
            : t('terminal.hardware.profile.view', 'View hardware')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="font-semibold">
          {t('terminal.hardware.profile.title', 'Group hardware defaults')}
        </h3>
        <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
          {t('common.close', 'Close')}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        {t(
          'terminal.hardware.profile.help',
          'Applies to every terminal on this profile that does not override the block. Blank means not authored — the till keeps its own build default, which is not the same as zero.',
        )}
      </p>

      {blocks.map((block) => (
        <section key={block} className="rounded-md border p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h4 className="text-sm font-medium">
              {t(`terminal.hardware.block.${block}`, PERIPHERAL_BLOCK_LABELS[block])}
            </h4>
            <span className="text-xs text-muted-foreground">
              {summarisePeripheralBlock(block, config, t)}
            </span>
          </div>
          <div className="mt-2">
            <PeripheralBlockFields
              block={block}
              // An unauthored block starts from its defaults, as the terminal
              // "set here" does: for SoftFiscal Android the production PKI and
              // i.EKA addresses (RL-957 Q5). Nothing is saved until it is edited.
              value={(config[block] ?? BLOCK_DEFAULTS[block]) as Record<string, unknown>}
              disabled={!canAdmin}
              onChange={(next) => {
                const copy: Record<string, unknown> = { ...config };
                if (Object.keys(next).length === 0) delete copy[block];
                else copy[block] = next;
                setDraft(copy as TerminalPeripheralConfig);
                setError(null);
              }}
            />
          </div>
        </section>
      ))}

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      {canAdmin ? (
        <div className="flex items-center gap-3">
          <Button
            disabled={!dirty || update.isPending}
            onClick={() => {
              setError(null);
              update.mutate(
                {
                  id: profile.id,
                  peripheralConfig: config,
                  expectedPeripheralConfigVersion: profile.peripheralConfigVersion ?? 1,
                },
                {
                  onSuccess: () => setDraft(null),
                  onError: (err) => setError(readServerMessage(err, t)),
                },
              );
            }}
          >
            {update.isPending
              ? t('common.saving', 'Saving…')
              : t('terminal.hardware.profile.save', 'Save group hardware')}
          </Button>
          {dirty && (
            <Button variant="outline" onClick={() => setDraft(null)}>
              {t('common.discardChanges', 'Discard changes')}
            </Button>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t(
            'terminal.hardware.profile.permission',
            'Editing group hardware needs terminal-management.admin.',
          )}
        </p>
      )}
    </div>
  );
}

function readServerMessage(
  err: unknown,
  t: (key: string, bakedEnglish?: string) => string,
): string {
  if (isAxiosError(err)) {
    const data: unknown = err.response?.data;
    if (typeof data === 'object' && data !== null && 'message' in data) {
      const message = (data as { message: unknown }).message;
      if (typeof message === 'string') return message;
      if (Array.isArray(message)) return message.join('; ');
    }
  }
  return t(
    'terminals.profilePeripheral.saveFailed',
    'Could not save the group hardware configuration.',
  );
}
